import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import {
    IconClose,
    IconStapleFront
} from '$partials/icons/index.js';

import { useKeyboard } from '$hooks/events.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const NavigationDialog = ({
    contact,
    handleClose,
    isNavOpen,
    links,
    pageType
}) => {
    const dialogRef = useRef();

    useKeyboard({
        'Escape': () => isNavOpen && handleClose()
    }, [ isNavOpen ]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        if (isNavOpen) {
            if (dialogNode && !dialogNode.open) {
                disableBodyScroll(dialogNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open) {
                enableBodyScroll(dialogNode);
                dialogNode.close?.();
            }
        }

        return () => {
            enableBodyScroll(dialogNode);
        };
    }, [ isNavOpen ]);

    return (
        <dialog
            className='bg-peach fixed top-0 left-0 right-0 bottom-0 min-w-full min-h-dynamic-screen'
            ref={dialogRef}>
            {/* Logo and close button */}
            <div className='layout-container'>
                <div className='flex justify-between items-start mt-20 md:mt-50'>
                    {pageType === 'Home' ? (
                        <div className='inline-flex'>
                            <IconStapleFront />
                        </div>
                    ) : (
                        <a
                            aria-label='Goto home page'
                            className='inline-flex'
                            href='/'>
                            <IconStapleFront />
                        </a>
                    )}

                    <button
                        aria-expanded={isNavOpen}
                        aria-label='Close main navigation'
                        className='cursor-pointer'
                        onClick={handleClose}
                        type='button'>
                        <IconClose/>
                    </button>
                </div>
            </div>

            {/* Navigation links */}
            <div className='layout-container'>
                {links?.length > 0 && (
                    <nav
                        aria-label='Main navigation links'
                        className='navigation layout-grid-negative-gutter sm:flex'>
                        <div className='hidden md:block w-1/12 layout-grid-col-padding' />

                        <ul className='w-full md:w-11/12 flex flex-wrap items-start gap-x-40 gap-y-25 sm:gap-y-30 layout-grid-col-padding mt-40 md:mt-70'>
                            {links.map((link, idx) => {
                                return (
                                    <li
                                        className='navigation__item w-full md:w-[calc(((100%/12)*4)-40px)]'
                                        key={idx}>
                                        <p className='navigation__item-heading type-style-3 mb-40'>
                                            {link.heading}
                                        </p>

                                        <div
                                            className='type-style-4'
                                            dangerouslySetInnerHTML={{ __html: link.text }} />
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>
                )}
            </div>

            <div className='layout-container'>
                <div className='layout-grid-negative-gutter mb-20 sm:flex mt-60 sm:mt-100'>
                    <div className='md:w-1/12 layout-grid-col-padding' />

                    <div className='layout-grid-col-padding'>
                        <div
                            className='rich-text rich-text--navigation type-style-4'
                            dangerouslySetInnerHTML={{ __html: contact}}/>
                    </div>
                </div>
            </div>
        </dialog>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    contact: PropTypes.string,
    handleClose: PropTypes.func.isRequired,
    isNavOpen: PropTypes.bool,
    links: PropTypes.array,
    pageType: PropTypes.string
};

NavigationDialog.propTypes = propTypes;

export default NavigationDialog;
